import { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { getCurrentFY } from '@/lib/fy';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Bank, Coins, Wallet, Plus, ArrowUp, ArrowDown, HandCoins, UserMinus, PiggyBank, Buildings, ArrowRight, CreditCard } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { AccountCard, typeMeta } from '@/components/AccountCard';
import SystemAccountDialog from '@/components/SystemAccountDialog';
import AccountLedgerDialog from '@/components/AccountLedgerDialog';
import IncomeExpenseDialog from '@/components/IncomeExpenseDialog';
import { useCan } from '@/lib/permissions';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

export default function FinancePage() {
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const isAdmin = user?.role === 'admin';
  const canEdit = useCan('balance_sheet', 'edit');
  const canDelete = useCan('balance_sheet', 'delete');
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // Active breakdown popup — opens the Income or Expense band's detail view.
  const [breakdownKind, setBreakdownKind] = useState(null); // 'income' | 'expense' | null

  const [showAddAccount, setShowAddAccount] = useState(false);
  const [ledgerAccount, setLedgerAccount] = useState(null);
  // System aggregate dialog (Debtors / Creditors). Holds 'debtors' | 'creditors' | null.
  const [sysDialog, setSysDialog] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [accForm, setAccForm] = useState({ name: '', type: 'cash', balance: '' });

  const [confirmDelete, setConfirmDelete] = useState(null);

  // P&L date-range state — defaults to the current Indian financial year. The user can
  // pick FY / Today / Week / Month / All / custom to drill into any past period; the
  // balance-sheet snapshot itself (account balances, advances) always reflects today.
  const _initFy = getCurrentFY();
  const [plFrom, setPlFrom] = useState(_initFy.start);
  const [plTo, setPlTo] = useState(_initFy.end);
  const [plPreset, setPlPreset] = useState('fy');
  const [plLabel, setPlLabel] = useState(_initFy.label);

  const applyPlPreset = (val) => {
    setPlPreset(val);
    const today = new Date();
    const toIso = (d) => d.toISOString().split('T')[0];
    if (val === 'all') { setPlFrom(''); setPlTo(''); setPlLabel('all-time'); return; }
    if (val === 'fy') { const fy = getCurrentFY(); setPlFrom(fy.start); setPlTo(fy.end); setPlLabel(fy.label); return; }
    if (val === 'today') { const d = toIso(today); setPlFrom(d); setPlTo(d); setPlLabel('today'); return; }
    if (val === 'week') {
      const start = new Date(today); start.setDate(start.getDate() - start.getDay());
      setPlFrom(toIso(start)); setPlTo(toIso(today)); setPlLabel('this week'); return;
    }
    if (val === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      setPlFrom(toIso(start)); setPlTo(toIso(today)); setPlLabel('this month'); return;
    }
  };

  const fetchData = useCallback(() => {
    const params = {};
    if (plFrom) params.from_date = plFrom;
    if (plTo) params.to_date = plTo;
    Promise.all([
      api.get('/finance/summary', { params }),
      api.get('/finance/accounts'),
    ]).then(([s, a]) => {
      setSummary(s.data);
      setAccounts(a.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, [plFrom, plTo]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // When editing an account that already has ledger entries, only name + type
  // are editable — the balance becomes read-only because it's derived live
  // from the ledger and any direct adjustment would silently drift.
  const editAccountLocked = !!(editAccount && (editAccount.txn_count || 0) > 0);

  // Add/Edit Account
  const handleAddAccount = async () => {
    if (!accForm.name) { toast.error('Account name required'); return; }
    try {
      if (editAccount) {
        await api.put(`/finance/accounts/${editAccount.id}`, {
          name: accForm.name, type: accForm.type,
          // Skip the balance field entirely when the account is locked.
          balance: (!editAccountLocked && accForm.balance !== '') ? parseFloat(accForm.balance) : undefined,
        });
        toast.success('Account updated');
      } else {
        await api.post('/finance/accounts', { name: accForm.name, type: accForm.type, balance: parseFloat(accForm.balance) || 0 });
        toast.success('Account created');
      }
      setShowAddAccount(false); setEditAccount(null);
      setAccForm({ name: '', type: 'cash', balance: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const openEditAccount = (acc) => {
    setEditAccount(acc);
    setAccForm({ name: acc.name, type: acc.type, balance: String(acc.balance) });
    setShowAddAccount(true);
  };

  const handleDeleteAccount = async (acc) => {
    setConfirmDelete({ item: acc, label: `Delete account "${acc.name}"? All transactions for this account will also be deleted.` });
  };

  const confirmDeleteAction = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/finance/accounts/${confirmDelete.item.id}`);
      toast.success('Account deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
    setConfirmDelete(null);
  };

  if (loading) return <div className="p-8 text-sm text-[#52525B]">Loading...</div>;

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-5" data-testid="finance-page">
      <div className="flex flex-wrap items-start justify-end gap-3">
        <div className="flex gap-2 flex-wrap">
          {canEdit && (
            <Button variant="outline" onClick={() => { setEditAccount(null); setAccForm({ name: '', type: 'cash', balance: '' }); setShowAddAccount(true); }}
              className="rounded-xl border-[#E4E4E7] text-[#52525B] hover:bg-[#F4F4F5]" data-testid="add-account-btn">
              <Plus size={14} className="mr-1" /> Create Account
            </Button>
          )}
        </div>
      </div>

      {/* P&L period picker — drives the Net Profit / Loss card. Defaults to current FY. */}
      <div className="flex flex-wrap items-center gap-2 rounded-lg border border-[#E4E4E7] bg-[#FAFAFA] px-3 py-2" data-testid="pl-period-picker">
        <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Net Profit Period:</span>
        <div className="flex gap-1.5 flex-wrap">
          {[
            { val: 'fy',    label: 'FY' },
            { val: 'today', label: 'Today' },
            { val: 'week',  label: 'This Week' },
            { val: 'month', label: 'This Month' },
            { val: 'all',   label: 'All Time' },
          ].map(p => (
            <Button key={p.val} size="sm" variant={plPreset === p.val ? 'default' : 'outline'}
              className={`rounded-lg text-xs h-7 ${plPreset === p.val ? 'bg-[#0A0A0A] text-white hover:bg-black/80 hover:text-white' : 'border-[#E4E4E7]'}`}
              onClick={() => applyPlPreset(p.val)}
              data-testid={`pl-preset-${p.val}`}>{p.label}</Button>
          ))}
        </div>
        <div className="flex items-center gap-1.5 ml-1">
          <Input type="date" value={plFrom} onChange={(e) => { setPlFrom(e.target.value); setPlPreset('custom'); setPlLabel('custom range'); }}
            className="h-7 rounded-md text-xs w-32" data-testid="pl-from-date" />
          <span className="text-xs text-[#52525B]">to</span>
          <Input type="date" value={plTo} onChange={(e) => { setPlTo(e.target.value); setPlPreset('custom'); setPlLabel('custom range'); }}
            className="h-7 rounded-md text-xs w-32" data-testid="pl-to-date" />
        </div>
        <span className="ml-auto text-[10px] uppercase tracking-[0.15em] text-[#52525B]">
          Showing <span className="font-bold text-[#0A0A0A]">{plLabel}</span>
        </span>
      </div>

      {/* Balance Sheet — Liabilities & Equity (Left)  |  Assets (Right) */}
      {(() => {
        const grouped = { creditor: [], capital: [], credit_card: [], cash: [], bank: [], wallet: [], debtor: [], asset: [] };
        accounts.forEach(a => { if (grouped[a.type]) grouped[a.type].push(a); });
        const sumOf = (key) => grouped[key].reduce((s, a) => s + a.balance, 0);
        // Liability-side accounts are stored as negative numbers (credit balances).
        // For the Balance Sheet view we display their absolute values so users
        // never see a stray '−' sign on Creditors / Capital totals.
        const liabSum = Math.abs(sumOf('creditor'));
        const capitalSum = Math.abs(sumOf('capital'));
        const cardSum = Math.abs(sumOf('credit_card'));
        // NOTE: advancesHeld is NO LONGER added separately — the Creditors group
        // already contains the system "Customer Adv." card whose balance comes
        // from the same customer.advance_balance pool. Adding it again here
        // would double-count the liability.
        // Profit definition is unified with the Dashboard:
        //   Profit = Billed Service Charge + Income − Expenses
        // The backend already computes this and returns it as `summary.profit`,
        // along with the underlying `billed_svc_charge`, `total_income`,
        // and `total_expense` for the breakdown footnote on the P&L card.
        const profit = (summary?.profit ?? 0);
        const liabilitiesTotal = liabSum + capitalSum + cardSum + profit;
        const assetTotal = sumOf('cash') + sumOf('bank') + sumOf('wallet') + sumOf('debtor') + sumOf('asset');
        const diff = Math.abs(assetTotal - liabilitiesTotal);
        const assetLow = assetTotal < liabilitiesTotal;
        const liabLow = liabilitiesTotal < assetTotal;
        // Opening Adjustments — a computed "plug" figure added to whichever
        // side is short so the Balance Sheet always balances. Represents
        // opening-balance entries that were made against accounts without a
        // matching capital-side counterpart (a common bookkeeping gap when
        // migrating older data). Non-destructive — no txn is created; the
        // number is derived on every load from live totals.
        const openingAdj = diff;
        const showAdjOnLiab = liabLow && openingAdj > 0.5;
        const showAdjOnAsset = assetLow && openingAdj > 0.5;
        const grandTotal = Math.max(liabilitiesTotal, assetTotal);

        const renderGroup = (key, label, allowEmpty = false) => {
          const list = grouped[key] || [];
          if (!allowEmpty && list.length === 0) return null;
          const rawSum = list.reduce((s, a) => s + a.balance, 0);
          const meta = typeMeta(key);
          // Liability-side groups (creditor/capital) display the magnitude so the
          // Balance Sheet header bar shows e.g. "Creditors  ₹5,000" instead of "−₹5,000".
          const sum = meta.side === 'liability' ? Math.abs(rawSum) : rawSum;
          return (
            <div key={key} className="space-y-2">
              <div className={`flex items-center justify-between px-3 py-1.5 rounded-md ${meta.barBg} text-white shadow-sm`}>
                <p className="text-sm uppercase tracking-[0.18em] font-bold">{label}</p>
                <p className="text-sm font-mono font-bold">{fmt(sum)}</p>
              </div>
              {list.length === 0 ? (
                <p className="text-xs text-[#A1A1AA] italic px-1">No {label.toLowerCase()} accounts</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {list.map(acc => {
                    // Cards expose Edit always (name + type), Delete only when
                    // the account has zero ledger entries (`txn_count === 0`).
                    // System aggregates (Debtors / Creditors) remain view-only.
                    const isSys = !!acc.system;
                    const hasTxns = (acc.txn_count || 0) > 0;
                    return (
                      <AccountCard
                        key={acc.id}
                        acc={acc}
                        onView={isSys ? () => setSysDialog(acc.system) : setLedgerAccount}
                        onEdit={isSys ? undefined : (canEdit ? openEditAccount : undefined)}
                        onDelete={isSys || hasTxns ? undefined : (canDelete ? handleDeleteAccount : undefined)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          );
        };

        const openingAdjRow = (
          <div className="flex items-center justify-between px-3 py-1.5 rounded-md bg-[#6B7280] text-white shadow-sm" data-testid="opening-adjustments-row"
               title="Auto-computed 'plug' figure — balances opening-balance entries that lack a matching capital-side counterpart.">
            <p className="text-sm uppercase tracking-[0.18em] font-bold">Opening Adjustments</p>
            <p className="text-sm font-mono font-bold">{fmt(openingAdj)}</p>
          </div>
        );

        const PLCard = () => (
          <Card className={`rounded-xl border-2 shadow-sm ${profit >= 0 ? 'border-[#10B981]/40 bg-[#ECFDF5]' : 'border-[#DC2626]/40 bg-[#FEF2F2]'}`} data-testid="pl-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className={`w-9 h-9 rounded-xl ${profit >= 0 ? 'bg-[#D1FAE5]' : 'bg-[#FEE2E2]'} flex items-center justify-center`}>
                  {profit >= 0 ? <ArrowUp size={18} className="text-[#10B981]" /> : <ArrowDown size={18} className="text-[#DC2626]" />}
                </div>
                <Badge className={`rounded-md text-[8px] border-0 ${profit >= 0 ? 'bg-[#D1FAE5] text-[#10B981]' : 'bg-[#FEE2E2] text-[#DC2626]'}`} variant="outline">
                  {profit >= 0 ? 'profit' : 'loss'}
                </Badge>
              </div>
              <p className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">Net Profit / Loss <span className="text-[#A1A1AA] normal-case">({plLabel})</span></p>
              <div className="flex items-center justify-between mt-0.5">
                <p className={`text-xl font-bold tracking-tight font-mono ${profit >= 0 ? 'text-[#10B981]' : 'text-[#DC2626]'}`} style={{ fontFamily: 'var(--font-heading)' }}>{fmt(profit)}</p>
                <p className="text-[10px] text-[#52525B]" title="Billed Service Charge + Income − Expenses">
                  <span className="text-[#10B981] font-semibold">+{fmt(summary?.billed_svc_charge || 0)}</span>
                  {' svc + '}
                  <span className="text-[#10B981] font-semibold">{fmt(summary?.total_income || 0)}</span>
                  {' inc / '}
                  <span className="text-[#EF4444] font-semibold">-{fmt(summary?.total_expense || 0)}</span>
                </p>
              </div>
            </CardContent>
          </Card>
        );

        return (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            {/* LEFT: Liabilities & Equity */}
            <div className="flex flex-col" data-testid="balance-sheet-liabilities">
              {/* Expense band — sits above the "Liabilities & Equity" heading.
                  Click View to open the per-entry breakdown in a popup. */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between px-3 py-1.5 rounded-md bg-[#DC2626] text-white shadow-sm">
                  <p className="text-sm uppercase tracking-[0.18em] font-bold">Expenses</p>
                  <p className="text-sm font-mono font-bold">{fmt(summary?.total_expense || 0)}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setBreakdownKind('expense')}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-[#DC2626]/30 bg-[#FEF2F2] hover:bg-[#FEE2E2] transition"
                  data-testid="view-expense-band"
                >
                  <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#7F1D1D]">View Expense Entries</span>
                  <ArrowRight size={14} className="text-[#7F1D1D]" weight="bold" />
                </button>
              </div>
              <div className="flex items-baseline justify-between border-b-2 border-[#0A0A0A] pb-2 mb-4">
                <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Liabilities &amp; Equity</h2>
                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">What we owe / our capital</span>
              </div>

              <div className="space-y-4">
                {renderGroup('creditor', 'Creditors', true)}
                {renderGroup('capital', 'Capital', true)}
                {renderGroup('credit_card', 'Credit Cards')}
              </div>

              {/* Pinned to bottom: P&L → Difference (if liab < asset) → Total */}
              <div className="mt-auto pt-4 space-y-3">
                <PLCard />
                {showAdjOnLiab && openingAdjRow}
                <div className="flex items-center justify-between pt-3 border-t-2 border-[#0A0A0A]" data-testid="liabilities-total">
                  <p className="text-sm font-bold uppercase tracking-[0.1em]" style={{ fontFamily: 'var(--font-heading)' }}>Total Liabilities &amp; Equity</p>
                  <p className="text-xl font-bold font-mono">{fmt(grandTotal)}</p>
                </div>
              </div>
            </div>

            {/* RIGHT: Assets */}
            <div className="flex flex-col" data-testid="balance-sheet-assets">
              {/* Income band — sits above the "Assets" heading. Click View to
                  open the breakdown (billed svc charge + entries) popup. */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between px-3 py-1.5 rounded-md bg-[#10B981] text-white shadow-sm">
                  <p className="text-sm uppercase tracking-[0.18em] font-bold">Income</p>
                  <p className="text-sm font-mono font-bold">{fmt((summary?.billed_svc_charge || 0) + (summary?.total_income || 0))}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setBreakdownKind('income')}
                  className="w-full flex items-center justify-between px-3 py-2 rounded-md border border-[#10B981]/30 bg-[#ECFDF5] hover:bg-[#D1FAE5] transition"
                  data-testid="view-income-band"
                >
                  <span className="text-[11px] uppercase tracking-[0.14em] font-semibold text-[#065F46]">View Income Entries</span>
                  <ArrowRight size={14} className="text-[#065F46]" weight="bold" />
                </button>
              </div>
              <div className="flex items-baseline justify-between border-b-2 border-[#0A0A0A] pb-2 mb-4">
                <h2 className="text-lg font-bold tracking-tight" style={{ fontFamily: 'var(--font-heading)' }}>Assets</h2>
                <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-[#52525B]">What we own / receivables</span>
              </div>

              <div className="space-y-4">
                {renderGroup('cash', 'Cash')}
                {renderGroup('bank', 'Bank')}
                {renderGroup('wallet', 'Wallets')}
                {renderGroup('debtor', 'Debtors')}
                {renderGroup('asset', 'Fixed Assets')}
              </div>

              {/* Pinned to bottom: Difference (if asset < liab) → Total */}
              <div className="mt-auto pt-4 space-y-3">
                {showAdjOnAsset && openingAdjRow}
                <div className="flex items-center justify-between pt-3 border-t-2 border-[#0A0A0A]" data-testid="assets-total">
                  <p className="text-sm font-bold uppercase tracking-[0.1em]" style={{ fontFamily: 'var(--font-heading)' }}>Total Assets</p>
                  <p className="text-xl font-bold font-mono">{fmt(grandTotal)}</p>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Add/Edit Account Dialog */}
      <Dialog open={showAddAccount} onOpenChange={(v) => { setShowAddAccount(v); if (!v) setEditAccount(null); }}>
        <DialogContent className="rounded-xl border-[#E4E4E7]">
          <DialogHeader><DialogTitle>{editAccount ? 'Edit Account' : 'Add Account'}</DialogTitle><DialogDescription>{editAccount ? 'Update account details' : 'Create a new account'}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">Account Name *</Label><Input value={accForm.name} onChange={e => setAccForm({ ...accForm, name: e.target.value })} placeholder="e.g., SBI Savings, Petty Cash" className="rounded-lg mt-1" data-testid="acc-name-input" /></div>
            <div>
              <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B] mb-2 block">Account Type *</Label>
              <div className="grid grid-cols-4 gap-2">
                {[
                  { val: 'cash', label: 'Cash', icon: Coins, color: 'bg-[#D97706]' },
                  { val: 'bank', label: 'Bank', icon: Bank, color: 'bg-[#2563EB]' },
                  { val: 'wallet', label: 'Wallet', icon: Wallet, color: 'bg-[#7C3AED]' },
                  { val: 'debtor', label: 'Debtor', icon: HandCoins, color: 'bg-[#DB2777]' },
                  { val: 'creditor', label: 'Creditor', icon: UserMinus, color: 'bg-[#DC2626]' },
                  { val: 'capital', label: 'Capital', icon: PiggyBank, color: 'bg-[#16A34A]' },
                  { val: 'credit_card', label: 'Cred Card', icon: CreditCard, color: 'bg-[#BE185D]' },
                  { val: 'asset', label: 'Asset', icon: Buildings, color: 'bg-[#4F46E5]' },
                ].map(({ val, label, icon: Icon, color }) => (
                  <Button key={val} variant={accForm.type === val ? 'default' : 'outline'} className={`rounded-lg h-12 flex flex-col gap-1 ${accForm.type === val ? `${color} text-white hover:opacity-90` : 'border-[#E4E4E7]'}`}
                    onClick={() => setAccForm({ ...accForm, type: val })} data-testid={`acc-type-${val}`}><Icon size={18} /><span className="text-xs">{label}</span></Button>
                ))}
              </div>
            </div>
            {editAccountLocked ? (
              <div className="rounded-lg border border-[#FCD34D] bg-[#FFFBEB] p-3" data-testid="balance-locked-notice">
                <p className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#92400E]">Balance</p>
                <p className="text-sm font-mono font-semibold text-[#92400E] mt-0.5">
                  ₹{Number(editAccount?.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
                <p className="text-[11px] text-[#92400E] mt-1">
                  Derived from {editAccount?.txn_count} ledger {editAccount?.txn_count === 1 ? 'entry' : 'entries'} — only name &amp; type can be edited.
                </p>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-[0.1em] font-semibold text-[#52525B]">{editAccount ? 'Balance' : 'Opening Balance'}</Label>
                <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#A1A1AA]">&#8377;</span>
                  <Input type="number" value={accForm.balance} onChange={e => setAccForm({ ...accForm, balance: e.target.value })} placeholder="0" className="rounded-lg pl-7 font-mono" data-testid="acc-balance-input" /></div>
              </div>
            )}
          </div>
          <DialogFooter><Button onClick={handleAddAccount} className="bg-[#3B82F6] text-white hover:bg-[#2563EB] hover:text-white rounded-xl" data-testid="save-account-btn">{editAccount ? 'Update' : 'Create'} Account</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(v) => { if (!v) setConfirmDelete(null); }}>
        <AlertDialogContent className="rounded-xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Delete</AlertDialogTitle>
            <AlertDialogDescription>{confirmDelete?.label}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-lg" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAction} className="bg-[#EF4444] text-white hover:bg-[#DC2626] rounded-lg" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Full-page ledger popup — opens when a card's eye icon is clicked */}
      <AccountLedgerDialog
        account={ledgerAccount}
        open={!!ledgerAccount}
        onOpenChange={(o) => !o && setLedgerAccount(null)}
      />

      {/* Aggregate Debtors / Creditors drill-down — opens via View on the
          two system cards seeded by the backend. */}
      <SystemAccountDialog
        kind={sysDialog}
        open={!!sysDialog}
        onOpenChange={(o) => !o && setSysDialog(null)}
      />

      {/* Income / Expense breakdown popup — opens from the new band cards. */}
      <IncomeExpenseDialog
        kind={breakdownKind || 'income'}
        open={!!breakdownKind}
        onOpenChange={(o) => !o && setBreakdownKind(null)}
        summary={summary}
        fromDate={plFrom}
        toDate={plTo}
      />
    </div>
  );
}
